import T from 'Components/Helpers/translate';

/**
 * The build ledger: one card per tool call the page-authoring agent makes.
 *
 * Nothing here needs a backend change. `tool_use` / `tool_result` are already
 * appended to `agent_run_event` by the shared runtime (runAgent) and published
 * over Pusher, and `useAgentStream` already stores every event it sees so the
 * gap-free frontier is computed over all of them — they were simply dropped on
 * the floor apart from the transient `status` line. A settled turn carries the
 * same list a second way, as `metadata.toolTrace` on the persisted assistant
 * message, so the ledger survives a reload without an extra fetch.
 */

export type PageActivityStatus = 'running' | 'done' | 'error';

export type PageActivity = {
	/**
	 * Pairs a `tool_use` with its `tool_result`. Claude runs tools in parallel,
	 * so the two events interleave with other calls' events and cannot be paired
	 * by adjacency — `callId` is recorded on both for exactly this (WEB-7817).
	 */
	callId: string;
	name: string;
	label: string;
	status: PageActivityStatus;
	/** Present only when `status === 'error'`; this is what the fix button sends back. */
	error?: string;
	durationMs?: number;
};

export type FoldableRunEvent = {
	type: string;
	text: string | null;
	payload?: Record<string, unknown> | null;
	/** `regDate` as epoch ms, when known — used only to show a duration. */
	at?: number | null;
};

// Longest error we put on a card. The full text still goes to the agent when the
// user clicks fix — this only bounds what a single row renders.
const ERROR_DISPLAY_MAX = 160;

// Shortest call worth timing. Anything faster rounds to "0,0 s", which reads as a
// measurement but carries none.
const DURATION_FLOOR_MS = 100;

/**
 * Human label for a tool call, in the user's language.
 *
 * The live path rarely reaches this: every tool call is preceded by a `status`
 * event carrying the runtime's own label ("Looking up activity…"), which we use
 * verbatim rather than restating the server's map here. This is the fallback for
 * a settled turn replayed from `toolTrace`, which records no status line.
 */
export function toolLabel(name: string): string {
	const key = `assistant.pages.activity.tool.${name}`;
	const translated = T(key);
	// `T` echoes the key back when there's no entry — anything not in the map
	// falls through to the tool's own name rather than rendering `assistant.…`.
	return translated === key ? T('assistant.pages.activity.tool.generic', { name }) : translated;
}

/** Pull the message out of a tool result, which may be an `{ "error": … }` envelope. */
function errorText(output: unknown): string {
	if (typeof output !== 'string') return T('assistant.pages.activity.errorGeneric');
	try {
		const parsed = JSON.parse(output);
		const msg = parsed?.error ?? parsed?.message;
		if (typeof msg === 'string' && msg.trim()) return msg.trim();
	} catch {
		// Not JSON — the raw string is the best error we have.
	}
	return output.trim() || T('assistant.pages.activity.errorGeneric');
}

function truncate(text: string): string {
	return text.length > ERROR_DISPLAY_MAX ? `${text.slice(0, ERROR_DISPLAY_MAX - 1)}…` : text;
}

/**
 * Fold a run's recorded events into the ledger.
 *
 * Walks in `seq` order (a single monotonic per-run counter across every event
 * type) and pairs each `tool_use` with the `tool_result` carrying the same
 * `callId`. A `status` immediately precedes its own `tool_use`, so the pending
 * status is consumed as that call's label.
 *
 * A `tool_use` with no matching result yet is the call currently in flight —
 * that is what drives the spinner, so it is deliberately kept rather than
 * filtered out.
 */
export function foldPageActivities(events: Map<number, FoldableRunEvent>): PageActivity[] {
	const seqs = [...events.keys()].sort((a, b) => a - b);
	const order: string[] = [];
	const byCall = new Map<string, PageActivity & { startedAt?: number | null }>();
	let pendingStatus: string | null = null;

	for (const seq of seqs) {
		const ev = events.get(seq);
		if (!ev) continue;
		const payload = ev.payload ?? null;

		if (ev.type === 'status') {
			pendingStatus = ev.text?.trim() || null;
			continue;
		}

		if (ev.type === 'tool_use') {
			const name = typeof payload?.name === 'string' ? payload.name : '';
			// Without a callId there is nothing to pair the result to, so the row
			// would spin forever. Synthesise one from the seq, which is unique.
			const callId = typeof payload?.callId === 'string' ? payload.callId : `seq_${seq}`;
			if (byCall.has(callId)) continue;
			byCall.set(callId, {
				callId,
				name,
				label: pendingStatus || toolLabel(name),
				status: 'running',
				startedAt: ev.at ?? null
			});
			order.push(callId);
			pendingStatus = null;
			continue;
		}

		if (ev.type === 'tool_result') {
			const callId = typeof payload?.callId === 'string' ? payload.callId : null;
			const item = callId ? byCall.get(callId) : undefined;
			if (!item) continue;
			const isError = payload?.isError === true;
			item.status = isError ? 'error' : 'done';
			if (isError) item.error = truncate(errorText(payload?.output));
			if (item.startedAt != null && ev.at != null) {
				const ms = ev.at - item.startedAt;
				// Below the floor there is nothing worth saying — "0,0 s" is noise
				// dressed as data. Above the ceiling the two timestamps can't both be
				// trusted. Either way, show no duration rather than a wrong one.
				if (ms >= DURATION_FLOOR_MS && ms < 10 * 60 * 1000) item.durationMs = ms;
			}
		}
	}

	return order.map(id => {
		const { startedAt: _startedAt, ...rest } = byCall.get(id) as PageActivity & { startedAt?: number | null };
		return rest;
	});
}

/**
 * Rebuild the ledger for a settled turn from the persisted assistant message.
 *
 * `runAgent` stores every call of the turn as `metadata.toolTrace` —
 * `{ name, input, output }` per call, truncated server-side. There is no status
 * line and no timing, so labels come from `toolLabel` and no duration is shown;
 * an `{ "error": … }` envelope in `output` still resolves to an error row.
 */
export function activitiesFromToolTrace(metadata: unknown): PageActivity[] {
	// `metadata` reaches this surface as a JSON STRING — the conversation endpoint
	// hands the column through unparsed — so the string branch is the live path
	// here, not a defensive afterthought. `readAttachmentMetadata` tolerates both
	// for the same reason; mirror it rather than assuming either shape.
	let parsed: unknown = metadata;
	if (typeof metadata === 'string') {
		try {
			parsed = JSON.parse(metadata);
		} catch {
			// Unparseable metadata just means no ledger to draw; the reply still renders.
			return [];
		}
	}
	const trace = (parsed as { toolTrace?: unknown } | null | undefined)?.toolTrace;
	if (!Array.isArray(trace)) return [];
	const out: PageActivity[] = [];
	trace.forEach((raw, i) => {
		const call = raw as { name?: unknown; output?: unknown };
		const name = typeof call?.name === 'string' ? call.name : '';
		if (!name) return;
		const failed = isErrorOutput(call?.output);
		out.push({
			callId: `trace_${i}`,
			name,
			label: toolLabel(name),
			status: failed ? 'error' : 'done',
			...(failed ? { error: truncate(errorText(call?.output)) } : {})
		});
	});
	return out;
}

function isErrorOutput(output: unknown): boolean {
	if (typeof output !== 'string') return false;
	try {
		const parsed = JSON.parse(output);
		return typeof parsed?.error === 'string' && parsed.error.trim().length > 0;
	} catch {
		// The persisted trace is truncated at TOOL_TRACE_OUTPUT_MAX (2000 chars) by
		// runAgent, so a long error envelope arrives cut mid-string and cannot parse.
		// Treating that as success is the worst outcome: the row rendered a green
		// check and no fix button for a call the user had just watched fail live,
		// where `payload.isError` was authoritative. Fall back to the shape.
		return /^\s*\{\s*"error"\s*:\s*"./.test(output);
	}
}

/**
 * The hidden turn the fix button sends. Mirrors `fixErrorPrompt` (the preview
 * crash banner) — prefixed `[System:` so it never shows as a user bubble — but
 * names the failing call, which is the part the agent needs to correct.
 */
export function retryToolPrompt(name: string, error: string): string {
	return (
		`[System: the \`${name}\` call failed with: ${error}. ` +
		'Work out why and fix it — correct the arguments and call it again, or take a different route to the same data. ' +
		'Do not explain the failure at length, just resolve it.]'
	);
}

/**
 * Lines of page code written so far in a streaming turn.
 *
 * This is the progress indicator that lets the code stop being one (WEB-10047):
 * counted off the real stream, so it only moves when the model actually emits,
 * and it lands on the same figure the finished diff card reports. Blank lines
 * are not progress; prose outside a fence is not page code.
 */
export function streamingCodeLineCount(raw: string): number {
	let count = 0;
	let inFence = false;
	for (const line of raw.split('\n')) {
		if (/^\s*```/.test(line)) {
			inFence = !inFence;
			continue;
		}
		if (inFence && line.trim()) count++;
	}
	return count;
}

/**
 * Machine-only context attached to a user message by the element picker.
 *
 * The structural detail (DOM path, position, the labelling text) is for the
 * agent; a person reading their own message back should not see
 * `DOM: div > div > div`. It rides in a ```pick fence so it can be stripped for
 * display while staying in the persisted content the agent reads — the same
 * split the ```suggestions fence already uses (WEB-10047).
 */
const PICK_BLOCK_RE = /\n*```pick[^\n]*\n[\s\S]*?```/;

export function stripPickBlock(content: string): string {
	return content.replace(PICK_BLOCK_RE, '').trim();
}

/**
 * What the picker chip above a sent message says.
 *
 * `kind` is the raw token (`column`, `heading`, …) that maps to a
 * `assistant.pages.pick.kind.*` label; `text` is the element's visible text, or
 * '' for something unlabelled like an image.
 */
export type PickedNote = { kind: string; text: string };

const PICK_FIELD_RE = /^(kind|text):\s*(.*)$/;

/**
 * Read the chip back out of a persisted message.
 *
 * The pick survives a reload because it lives in the message content, so the
 * fence is the single source for both the live and the reloaded transcript —
 * no metadata round-trip, and no backend change (WEB-10047).
 */
export function parsePickBlock(content: string): PickedNote | null {
	const fence = content.match(/```pick[^\n]*\n([\s\S]*?)```/);
	if (!fence) return null;
	const note: PickedNote = { kind: '', text: '' };
	for (const line of fence[1].split('\n')) {
		const field = line.match(PICK_FIELD_RE);
		if (field) note[field[1] as keyof PickedNote] = field[2].trim().replace(/^"|"$/g, '');
	}
	return note.kind || note.text ? note : null;
}
